import {create} from 'zustand'
import isEqual from "lodash/isEqual";

const mergeMachine = (machine, update) => {
    // Si les tâches sont fournies, remplacer complètement, sinon faire un merge partiel
    return update.tasks !== undefined ? update : {...machine, ...update}
}

const useMachineStore = create((set) => ({
    machines: [],
    // Instant de réception de la dernière mise à jour socket complète (avec tâches) par machine : un rechargement de l'API
    // parti avant cet instant revient avec un état plus ancien et ne doit pas l'écraser.
    socketUpdatedAt: {},
    // Instant de départ du dernier rechargement appliqué par machine : une mise à jour socket reçue avant est déjà incluse.
    snapshotRequestedAt: {},
    setMachines: (machines) => set(() => ({machines: machines})),
    setMachinesFromSnapshot: (snapshot, requestedAt) => set((state) => {
        const current = new Map(state.machines.map(machine => [machine.id, machine]))
        const snapshotRequestedAt = {...state.snapshotRequestedAt}

        const machines = snapshot.map(machine => {
            const existing = current.get(machine.id)
            if (existing && (state.socketUpdatedAt[machine.id] || 0) > requestedAt) {
                return existing
            }

            snapshotRequestedAt[machine.id] = Math.max(snapshotRequestedAt[machine.id] || 0, requestedAt)

            if (!existing) {
                return machine
            }

            return isEqual(existing, machine) ? existing : machine
        })

        return {machines, snapshotRequestedAt}
    }),
    updateMachine: (updatedMachine) => set((state) => {
        const index = state.machines.findIndex(machine => machine.id === updatedMachine.id)
        if (index === -1) {
            return state
        }

        const machines = state.machines.slice()
        machines[index] = mergeMachine(state.machines[index], updatedMachine)
        return {machines}
    }),
    // Réservé aux événements socket. Les actions locales (glisser, allumer) modifient parfois les objets du store sur place
    // avant d'appeler updateMachine : une comparaison les prendrait à tort pour des doublons et n'afficherait rien.
    applySocketUpdate: (updatedMachine, receivedAt = Date.now()) => set((state) => {
        const index = state.machines.findIndex(machine => machine.id === updatedMachine.id)
        if (index === -1) {
            return state
        }

        const isFullUpdate = updatedMachine.tasks !== undefined
        const lastSocketUpdate = state.socketUpdatedAt[updatedMachine.id] || 0

        // Une page cachée garde ses événements jusqu'au retour sur l'écran : reçus avant le dernier rechargement appliqué,
        // ou avant la dernière mise à jour complète, ils décriraient un état déjà dépassé.
        if (receivedAt < (state.snapshotRequestedAt[updatedMachine.id] || 0) || (isFullUpdate && receivedAt < lastSocketUpdate)) {
            return state
        }

        const machine = state.machines[index]
        const next = mergeMachine(machine, updatedMachine)
        const socketUpdatedAt = isFullUpdate && receivedAt > lastSocketUpdate
            ? {...state.socketUpdatedAt, [updatedMachine.id]: receivedAt}
            : state.socketUpdatedAt

        // Le NAS envoie souvent plusieurs update-machine identiques d'affilée : inutile de re-rendre pour rien.
        if (isEqual(machine, next)) {
            return socketUpdatedAt === state.socketUpdatedAt ? state : {socketUpdatedAt}
        }

        const machines = state.machines.slice()
        machines[index] = next
        return {machines, socketUpdatedAt}
    }),
    updateMachineTasks: (machineId, tasks) => set((state) => ({
        machines: state.machines.map((machine) => {
            return machine.id === machineId
                ? {...machine, tasks: tasks}
                : machine;
        }),
    })),
    getMachineById: (id) => {
        const state = useMachineStore.getState();
        return state.machines.find(m => m.id === id);
    }
}))

export default useMachineStore
